import type { PaymentMethodId } from '@/features/payment/availability';

/**
 * What the guest has decided so far, between one step of checkout and the next.
 *
 * In storage rather than in router state or a query string, for three reasons. A guest who reloads mid-flow
 * should not be sent back to the itemisation to choose a tip again. The payment gateways leave this origin
 * and come back, at which point nothing in memory survives - the legacy app persisted its context reducers
 * for exactly this reason. And an amount of money has no business in a URL, where it ends up in referrer
 * headers and server logs.
 *
 * Not a security boundary: a guest can edit this as easily as they can edit a query string. The server
 * re-validates the tip when the payment is made, and is the only authority on what gets charged.
 */
const STORAGE_KEY = 'touchtab.payment-draft';

export interface PaymentDraft {
    /**
     * The check this draft belongs to.
     *
     * Checked on read rather than assumed. A tip chosen for one check must never be applied to another - the
     * same failure the tip screen guards against in memory, which would otherwise come back through storage
     * the moment a guest scanned a second check on the same phone.
     */
    checkId: string;
    tipCents: number;
    method?: PaymentMethodId;

    /**
     * The payment the server minted, kept because the hosted-page round trip destroys the page that knew it.
     *
     * Without this the browser comes back from a gateway with no idea what it was doing, and the only way to
     * find out would be reading the gateway's response out of the query string — which is exactly what the
     * client must not do.
     */
    paymentId?: string;
}

export function saveDraft(draft: PaymentDraft): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
        // Private browsing, or storage full. The flow still works for this page load; only a reload or a
        // gateway round-trip loses the tip, and that beats failing the checkout outright.
    }
}

/**
 * The draft for this check, or null when there isn't a usable one.
 *
 * Anything doubtful - missing, malformed, or belonging to a different check - is treated as absent. The cost
 * of discarding is one more tap; the cost of keeping a wrong one is charging a guest the wrong amount.
 */
export function loadDraft(checkId: string): PaymentDraft | null {
    let raw: string | null = null;

    try {
        raw = localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }

    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as Partial<PaymentDraft>;

        if (
            typeof parsed?.checkId !== 'string' ||
            parsed.checkId !== checkId ||
            typeof parsed.tipCents !== 'number' ||
            !Number.isSafeInteger(parsed.tipCents) ||
            parsed.tipCents < 0
        ) {
            return null;
        }

        return parsed as PaymentDraft;
    } catch {
        clearDraft();
        return null;
    }
}

export function clearDraft(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing useful to do; the caller is already treating the draft as gone.
    }
}

